import React from "react";
import { Search, Settings, Sparkles } from "lucide-react";

import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ConnectorCategory } from "@/hooks/useConnectors";

// The rail exists because a flat list stops working the moment the catalogue outgrows one screen.
// Search sits at the top rather than the bottom: at eight connectors browsing wins, at eighty
// searching does, and the same rail has to serve both without being rebuilt.

export const FILTER_ENABLED = "__enabled__";
export const FILTER_ALL = "__all__";

interface ConnectorRailProps {
  search: string;
  onSearchChange: (value: string) => void;
  filter: string;
  onFilterChange: (filter: string) => void;
  enabledCount: number;
  totalCount: number;
  categories: ConnectorCategory[];
  isWorkspaceAdmin?: boolean;
  onRequestConnector: () => void;
  onOpenAdmin: () => void;
}

const ConnectorRail: React.FC<ConnectorRailProps> = ({
  search,
  onSearchChange,
  filter,
  onFilterChange,
  enabledCount,
  totalCount,
  categories,
  isWorkspaceAdmin,
  onRequestConnector,
  onOpenAdmin,
}) => {
  const row = (key: string, label: string, count?: number) => {
    const active = filter === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onFilterChange(key)}
        aria-current={active ? "true" : undefined}
        className={`flex shrink-0 items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors sm:w-full sm:shrink ${
          active
            ? "bg-white font-medium text-foreground shadow-sm ring-1 ring-gray-200 dark:bg-white/10 dark:text-white dark:ring-white/10"
            : "text-muted-foreground hover:bg-white/70 hover:text-foreground dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white"
        }`}
      >
        <span className="truncate">{label}</span>
        {typeof count === "number" && (
          <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{count}</span>
        )}
      </button>
    );
  };

  return (
    <aside className="flex min-h-0 w-full shrink-0 flex-col border-b border-gray-100 bg-gray-50/80 sm:w-56 sm:border-b-0 sm:border-r dark:border-white/10 dark:bg-white/5">
      <div className="shrink-0 p-3 pb-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search"
            aria-label="Search connectors"
            className="h-8 bg-white pl-8 text-xs text-foreground placeholder:text-muted-foreground dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500"
          />
        </div>
      </div>

      <ScrollArea className="min-h-0 sm:flex-1">
        <nav
          className="flex gap-1 overflow-x-auto px-3 pb-2 sm:flex-col sm:gap-0.5 sm:space-y-0.5 sm:overflow-x-visible"
          aria-label="Connector categories"
        >
          {row(FILTER_ENABLED, "Enabled", enabledCount)}
          {row(FILTER_ALL, "All", totalCount)}

          {categories.length > 0 && (
            <p className="hidden px-2.5 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:block dark:text-gray-500">
              Categories
            </p>
          )}
          {categories.map((c) => row(c.key, c.label, c.count))}
        </nav>
      </ScrollArea>

      <div className="hidden shrink-0 space-y-2 border-t border-gray-200/70 p-3 sm:block dark:border-white/10">
        <div className="rounded-lg border border-gray-200 bg-white p-2.5 dark:border-white/10 dark:bg-white/5">
          <p className="flex items-center gap-1.5 text-[11px] font-medium text-foreground">
            <Sparkles className="h-3 w-3 text-plum" />
            Missing a connector?
          </p>
          <button
            type="button"
            onClick={onRequestConnector}
            className="mt-1.5 w-full rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:border-white/20 dark:hover:bg-white/10"
          >
            Request
          </button>
        </div>

        {isWorkspaceAdmin && (
          <button
            type="button"
            onClick={onOpenAdmin}
            className="flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-white hover:text-foreground dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <Settings className="h-3 w-3" />
            Admin settings
          </button>
        )}
      </div>
    </aside>
  );
};

export default ConnectorRail;
